/**
 * TaskTableView — UnifyTable list of FHIR Tasks.
 */

import { Anchor, Avatar, Badge, Group, Text } from '@mantine/core';
import type { Task } from '@medplum/fhirtypes';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UnifyTable, UnifyTableSortAffordance, type UnifyTableColumn } from '@/components/UnifyTable';
import { formatUsDateShort } from '@/lib/dateHelpers';
import { getTaskDisplayDescription } from '../../../lib/portalAppointmentTaskDisplay';
import { getTaskTypeLabel } from '../services/taskFhirMapping';
import type { TaskFilters, TaskPriority } from '../types';
import {
  TASK_PRIORITY_RANK,
  TaskInboxStatusLabels,
  TaskPriorityColors,
  TaskPriorityLabels,
  TaskStatusColors,
  toInboxStatus,
} from '../types';

type SortColumn = TaskFilters['sortBy'];

interface TaskTableViewProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  sortBy: SortColumn;
  sortDirection: 'asc' | 'desc';
  onSort: (column: SortColumn) => void;
  showAssignee?: boolean;
}

export function TaskTableView({
  tasks,
  onTaskClick,
  sortBy,
  sortDirection,
  onSort,
  showAssignee = true,
}: TaskTableViewProps): JSX.Element {
  const columns = useMemo((): UnifyTableColumn<Task>[] => {
    const sortableHeader = (col: SortColumn, label: string) => {
      const active = sortBy === col;
      return (
        <Group
          gap={6}
          wrap="nowrap"
          onClick={() => onSort(col)}
          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        >
          <Text component="span" size="sm" fw={600}>
            {label}
          </Text>
          <UnifyTableSortAffordance active={active} direction={sortDirection} />
        </Group>
      );
    };

    const cols: UnifyTableColumn<Task>[] = [
      {
        key: 'task',
        label: 'Task',
        render: (task) => (
          <Text size="sm" lineClamp={1}>
            {getTaskDisplayDescription(task, 'provider')}
          </Text>
        ),
        getValue: (task) => getTaskDisplayDescription(task, 'provider'),
      },
      {
        key: 'type',
        label: 'Type',
        width: 160,
        render: (task) => (
          <Badge variant="light" size="sm" color="gray">
            {getTaskTypeLabel(task)}
          </Badge>
        ),
        getValue: (task) => getTaskTypeLabel(task),
      },
      {
        key: 'status',
        label: 'Status',
        width: 130,
        renderHeader: () => sortableHeader('status', 'Status'),
        render: (task) => {
          const inbox = toInboxStatus(task.status);
          return (
            <Badge variant="light" size="sm" color={TaskStatusColors[inbox]}>
              {TaskInboxStatusLabels[inbox]}
            </Badge>
          );
        },
        getValue: (task) => toInboxStatus(task.status),
      },
      {
        key: 'priority',
        label: 'Priority',
        width: 120,
        renderHeader: () => sortableHeader('priority', 'Priority'),
        render: (task) => {
          const priority = (task.priority as TaskPriority) || 'routine';
          return (
            <Badge variant="light" size="sm" color={TaskPriorityColors[priority]}>
              {TaskPriorityLabels[priority]}
            </Badge>
          );
        },
        getValue: (task) => String(TASK_PRIORITY_RANK[(task.priority as TaskPriority) || 'routine']),
      },
      {
        key: 'dueDate',
        label: 'Due',
        width: 120,
        renderHeader: () => sortableHeader('dueDate', 'Due'),
        render: (task) => (
          <Text size="sm">
            {task.restriction?.period?.end
              ? formatUsDateShort(task.restriction.period.end) || '—'
              : '—'}
          </Text>
        ),
        getValue: (task) => task.restriction?.period?.end ?? '',
      },
    ];

    if (showAssignee) {
      cols.push({
        key: 'assignee',
        label: 'Assignee',
        width: 160,
        render: (task) => {
          const name = task.owner?.display || 'Unassigned';
          return (
            <Group gap={6} wrap="nowrap">
              <Avatar size={18} radius="xl">
                {getInitials(name)}
              </Avatar>
              <Text size="sm" truncate>
                {name}
              </Text>
            </Group>
          );
        },
        getValue: (task) => task.owner?.display || 'Unassigned',
      });
    }

    cols.push(
      {
        key: 'client',
        label: 'Client',
        width: 140,
        render: (task) => {
          const patientName = task.for?.display;
          const clientRef = task.for?.reference;
          const clientId = clientRef?.startsWith('Patient/') ? clientRef.split('/')[1] : undefined;
          if (clientId) {
            return (
              <Anchor
                component={Link}
                to={`/dashboard/clients/${clientId}`}
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                {patientName}
              </Anchor>
            );
          }
          return (
            <Text size="sm" c="dimmed" truncate>
              {patientName || '—'}
            </Text>
          );
        },
        getValue: (task) => task.for?.display || '',
      },
    );

    return cols;
  }, [onSort, showAssignee, sortBy, sortDirection]);

  return (
    <UnifyTable
      withPaper={false}
      columns={columns}
      data={tasks}
      getRowKey={(task) => task.id ?? ''}
      pagination
      defaultRowsPerPage={25}
      onRowClick={onTaskClick}
      emptyMessage="No tasks found"
    />
  );
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
